import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Box, CircularProgress, Typography } from '@mui/material';

interface ProtectedRouteProps {
  children: React.ReactNode;
  requiredRole?: string[];
}

export const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ 
  children, 
  requiredRole 
}) => {
  const { state } = useAuth();
  const location = useLocation();

  const getAuthFromLocalStorage = () => {
    try {
      const authUser = localStorage.getItem('auth_user');
      if (authUser) {
        return JSON.parse(authUser);
      }
    } catch (error) {
      console.error('Error parsing auth_user from localStorage:', error);
    }
    return null;
  };

  const localStorageUser = getAuthFromLocalStorage();
  
  // Отладочная информация
  console.log('Auth state from context:', state);
  console.log('Auth user from localStorage:', localStorageUser);
  console.log('Required roles:', requiredRole);

  // Показываем загрузку пока проверяется авторизация
  if (state.isLoading) {
    return (
      <Box 
        sx={{ 
          display: 'flex', 
          flexDirection: 'column',
          alignItems: 'center', 
          justifyContent: 'center', 
          minHeight: '100vh',
          gap: 2
        }}
      >
        <CircularProgress size={48} />
        <Typography variant="body1" color="text.secondary">
          Проверка авторизации...
        </Typography>
      </Box>
    );
  }

  // Если пользователь не авторизован в контексте, но есть в localStorage
  // Не перенаправляем сразу, а используем данные из localStorage
  const effectiveUser = state.user || localStorageUser;

  if (!effectiveUser) {
    return <Navigate to="/auth" state={{ from: location }} replace />;
  }

  // Проверяем роли, если они указаны
  if (requiredRole && requiredRole.length > 0) {
    const userRoles: string[] = [];
    
    // Используем тип пользователя из контекста или localStorage
    const userType = state.user?.type || localStorageUser?.type;
    
    if (userType === 'user') {
      userRoles.push('user');
    } else if (userType === 'manager') {
      userRoles.push('manager');
      // Добавляем дополнительные роли менеджера, если они есть
      if (state.user?.manager?.roles) {
        userRoles.push(...state.user.manager.roles);
      }
      // Также проверяем роли в localStorage
      else if (localStorageUser?.manager?.roles) {
        userRoles.push(...localStorageUser.manager.roles);
      }
    }

    console.log('User type:', userType);
    console.log('User roles:', userRoles);
    console.log('Required roles:', requiredRole);

    const hasRequiredRole = requiredRole.some(role => userRoles.includes(role));
    
    console.log('Has required role:', hasRequiredRole);

    if (!hasRequiredRole) {
      // Если у пользователя нет необходимой роли, перенаправляем его
      if (effectiveUser.type === 'user') {
        return <Navigate to="/executor/dashboard" replace />;
      } else if (effectiveUser.type === 'manager') {
        return <Navigate to="/organization-manager/dashboard" replace />;
      } else {
        // Если тип пользователя не определен или что-то пошло не так, вернем на страницу авторизации
        return <Navigate to="/auth" replace />;
      }
    }
  }

  return <>{children}</>;
};